import { Separator } from "@radix-ui/react-separator";
import {
  ArrowUpIcon,
  ChevronDown,
  Keyboard,
  Mic,
  MicOff,
  PhoneIcon,
  XIcon,
} from "lucide-react";

import { Button } from "../../ui/button";
import { cn } from "../../ui/utils";
import { AgentState } from "../realtime-conversation-bar";
import { Card } from "./card";
import { LiveWaveform } from "./live-waveform";
import { Textarea } from "./textarea";

type BothViewProps = {
  className?: string;
  keyboardOpen: boolean;
  setKeyboardOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  waveformClassName?: string;
  micStream: MediaStream | null;
  agentState: AgentState;
  isConnected: boolean;
  isMuted: boolean;
  onToggleMute: () => void;
  onEnd: () => void;
  onStartChat: () => void;
  onStartVoice: () => void;
  textInput: string;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
};

const BothConversationView = ({
  className,
  keyboardOpen,
  setKeyboardOpen,
  waveformClassName,
  micStream,
  agentState,
  isConnected,
  isMuted,
  onToggleMute,
  onEnd,
  onStartChat,
  onStartVoice,
  textInput,
  onTextChange,
  onKeyDown,
  onSend,
}: BothViewProps) => {
  return (
    <div className={cn("flex w-full items-end justify-center pt-4", className)}>
      <Card className="m-0 w-full gap-0 border-0 p-0">
        <div className="flex flex-col-reverse">
          <div>
            {keyboardOpen && <Separator />}
            <div className="flex items-center justify-between gap-2 p-2">
              <div className="h-8 w-[120px] md:h-10">
                <div
                  className={cn(
                    "flex h-full items-center gap-2 rounded-md py-1",
                    "bg-foreground/5 text-foreground/70",
                  )}
                >
                  <div className="h-full flex-1">
                    <div
                      className={cn(
                        "relative flex h-full w-full shrink-0 items-center justify-center overflow-hidden rounded-sm",
                        waveformClassName,
                      )}
                    >
                      <LiveWaveform
                        key={agentState === "disconnected" ? "idle" : "active"}
                        stream={micStream}
                        active={Boolean(micStream) && isConnected && !isMuted}
                        processing={agentState === "connecting"}
                        barWidth={3}
                        barGap={1}
                        barRadius={4}
                        fadeEdges
                        fadeWidth={24}
                        sensitivity={1.8}
                        smoothingTimeConstant={0.85}
                        height={20}
                        mode="static"
                        className={cn(
                          "h-full w-full transition-opacity duration-300",
                          agentState === "disconnected" && "opacity-0",
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <Button
                  variant="widget"
                  size="icon"
                  onClick={onToggleMute}
                  aria-pressed={isMuted}
                  className={cn("cursor-pointer", {
                    "bg-foreground/5": isMuted,
                  })}
                  disabled={!isConnected}
                >
                  {isMuted ? <MicOff /> : <Mic />}
                </Button>

                <Button
                  variant="widget"
                  size="icon"
                  onClick={() => setKeyboardOpen((v) => !v)}
                  aria-pressed={keyboardOpen}
                  className="relative"
                >
                  <Keyboard
                    className={
                      "ease-[cubic-bezier(0.22,1,0.36,1)] h-5 w-5 transform-gpu transition-all duration-200 " +
                      (keyboardOpen
                        ? "scale-75 opacity-0"
                        : "scale-100 opacity-100")
                    }
                  />
                  <ChevronDown
                    className={
                      "delay-50 ease-[cubic-bezier(0.34,1.56,0.64,1)] absolute inset-0 m-auto h-5 w-5 transform-gpu transition-all duration-200 " +
                      (keyboardOpen
                        ? "scale-100 opacity-100"
                        : "scale-75 opacity-0")
                    }
                  />
                </Button>

                <Separator orientation="vertical" className="-my-2.5 mx-1" />

                {agentState === "disconnected" ? (
                  <>
                    <Button
                      variant="widget"
                      size="sm"
                      onClick={onStartChat}
                      className="text-xs"
                    >
                      Start Chat
                    </Button>

                    <Button variant="widget" size="icon" onClick={onStartVoice}>
                      <PhoneIcon className="h-5 w-5" />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="widget"
                    size="icon"
                    onClick={onEnd}
                    disabled={agentState === "disconnecting"}
                  >
                    <XIcon className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div
            className={cn(
              "overflow-hidden transition-all duration-300 ease-out",
              keyboardOpen ? "max-h-[120px]" : "max-h-0",
            )}
          >
            <div className="relative px-2 pb-2 pt-2">
              <Textarea
                value={textInput}
                onChange={onTextChange}
                onKeyDown={onKeyDown}
                placeholder={
                  isConnected
                    ? "Enter your message..."
                    : "Start a session first..."
                }
                className="min-h-25 resize-none border-0 pr-12 shadow-none focus-visible:ring-0"
                disabled={!isConnected}
              />
              <Button
                size="icon"
                variant="widget"
                onClick={onSend}
                disabled={!textInput.trim() || !isConnected}
                className="absolute bottom-3 right-3 h-8 w-8"
              >
                <ArrowUpIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BothConversationView;
